import React, { useMemo } from 'react';
import { CalendarClock, AlertTriangle, ChevronRight } from 'lucide-react';
import { PTPRecord } from '../../types';
import { todayIso } from '../../utils/accountFilters';
import { formatINR } from '../../utils/watermark';

/**
 * "These customers promised to pay TODAY."
 *
 * Your line 3. It could not have been built before now, because "today" was
 * the 23rd of August in nine places across the app -- a date left behind by
 * the demo data. A due-today count computed against that date is zero every
 * day of the year, so this banner would have sat there reporting nothing and
 * looking like it worked.
 *
 * It reads the PTPs already in memory, so it costs no extra database reads.
 * Overdue promises are shown alongside, because a promise broken yesterday is
 * more urgent than one due this afternoon.
 */

export interface PTPDueTodayBannerProps {
  ptps: PTPRecord[];
  /** Opens the PTP screen. */
  onOpen?: () => void;
}

export const PTPDueTodayBanner: React.FC<PTPDueTodayBannerProps> = ({ ptps, onOpen }) => {
  const today = todayIso();

  const { dueToday, overdue, dueAmount, overdueAmount } = useMemo(() => {
    const pending = (ptps || []).filter((p) => p && p.status === 'Pending');
    const d = pending.filter((p) => p.ptpDate === today);
    const o = pending.filter((p) => !!p.ptpDate && p.ptpDate < today);
    const sum = (list: PTPRecord[]) =>
      list.reduce((t, p) => t + (Number(p.amount) || 0), 0);
    return {
      dueToday: d,
      overdue: o,
      dueAmount: sum(d),
      overdueAmount: sum(o),
    };
  }, [ptps, today]);

  if (dueToday.length === 0 && overdue.length === 0) return null;

  return (
    <div
      className={`rounded-2xl border p-4 space-y-3 text-xs shadow-xs ${
        overdue.length > 0
          ? 'bg-rose-50/70 border-rose-200'
          : 'bg-amber-50/70 border-amber-200'
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {overdue.length > 0 ? (
            <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
          ) : (
            <CalendarClock className="w-4 h-4 text-amber-600 shrink-0" />
          )}
          <div>
            <h3
              className={`font-bold text-sm ${
                overdue.length > 0 ? 'text-rose-900' : 'text-amber-900'
              }`}
            >
              {dueToday.length > 0 && (
                <>
                  {dueToday.length} promise{dueToday.length === 1 ? '' : 's'} to pay due today
                  {dueAmount > 0 ? ` · ${formatINR(dueAmount)}` : ''}
                </>
              )}
              {dueToday.length > 0 && overdue.length > 0 ? ' · ' : ''}
              {overdue.length > 0 && (
                <>
                  {overdue.length} overdue
                  {overdueAmount > 0 ? ` · ${formatINR(overdueAmount)}` : ''}
                </>
              )}
            </h3>
            <p className="text-[11px] text-slate-600">
              {today}
              {overdue.length > 0
                ? ' — overdue promises are listed first; they were due before today and are still pending.'
                : ''}
            </p>
          </div>
        </div>

        {onOpen && (
          <button
            type="button"
            onClick={onOpen}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 rounded-xl font-bold text-slate-700 transition shrink-0"
          >
            <span>Open PTP commitments</span>
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {[...overdue, ...dueToday].slice(0, 6).map((p, idx) => {
          const isOverdue = !!p.ptpDate && p.ptpDate < today;
          return (
            <div
              key={p.id || `ptp-due-${idx}`}
              className="bg-white border border-slate-200 rounded-xl px-3 py-2 flex items-center justify-between gap-2"
            >
              <div className="min-w-0">
                <div className="font-bold text-slate-900 truncate">{p.customerName}</div>
                <div className="text-[11px] text-slate-500 truncate">
                  <span className="font-mono">{p.accountId}</span>
                  {p.agentName ? ` · ${p.agentName}` : ''}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono font-bold text-slate-900">
                  {formatINR(Number(p.amount) || 0)}
                </div>
                <div
                  className={`text-[10px] font-bold ${
                    isOverdue ? 'text-rose-700' : 'text-amber-700'
                  }`}
                >
                  {isOverdue ? `due ${p.ptpDate}` : 'today'}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {overdue.length + dueToday.length > 6 && (
        <p className="text-[11px] text-slate-500">
          and {overdue.length + dueToday.length - 6} more.
        </p>
      )}
    </div>
  );
};

export default PTPDueTodayBanner;
